/*
 * Copyright (c) 2026. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import { formValue } from "../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import { requiredFieldDefault, textFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import _ from "lodash";
import { Box } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const AlimonyClaims = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    hasAlimonyClaims: pcs.hasAlimonyClaims,
    "alimonyObligatedPerson.gender": pcs.alimonyObligatedPerson.gender,
    "alimonyObligatedPerson.givenName": pcs.alimonyObligatedPerson.givenName,
    "alimonyObligatedPerson.familyName": pcs.alimonyObligatedPerson.familyName,
    alimonyObligatedPersonRelation: pcs.alimonyObligatedPersonRelation,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Unterhalt</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Erhalten Sie von jemandem Unterhalt oder steht Ihnen Unterhalt zu? Hierbei geht es nur um
            Unterhaltszahlungen für Sie persönlich, nicht für Ihre Kinder.
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Anspruch"}
              name={"hasAlimonyClaims"}
              value={formValue(values, "hasAlimonyClaims")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              dependentValidationFields={[
                "alimonyObligatedPerson.gender",
                "alimonyObligatedPerson.givenName",
                "alimonyObligatedPerson.familyName",
                "alimonyObligatedPersonRelation",
              ]}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wie heißt die Person, die Ihnen Unterhalt zahlt oder zahlen müsste?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              displayEmpty
              label={"Anrede"}
              fullWidth
              value={formValue(values, "alimonyObligatedPerson.gender")}
              onChange={handleChange}
              name={`alimonyObligatedPerson.gender`}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              error={!!errors["alimonyObligatedPerson.gender"]}
              helperText={errors["alimonyObligatedPerson.gender"]}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              disabled={formValue(values, "hasAlimonyClaims") !== "yes"}
            >
              <MenuItem value={""}>Keine Anrede</MenuItem>
              <MenuItem value={"female"}>Frau</MenuItem>
              <MenuItem value={"male"}>Herr</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="alimonyObligatedPerson.givenName"
              label="Vorname"
              onBlur={handleBlur}
              validators={
                formValue(values, "hasAlimonyClaims") === "yes" ? [...requiredFieldDefault, ...textFieldDefault] : []
              }
              registerValidators={registerValidators}
              error={!!errors["alimonyObligatedPerson.givenName"]}
              helperText={errors["alimonyObligatedPerson.givenName"]}
              autoComplete="given-name"
              margin="normal"
              value={formValue(values, "alimonyObligatedPerson.givenName")}
              onChange={handleChange}
              disabled={formValue(values, "hasAlimonyClaims") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              name="alimonyObligatedPerson.familyName"
              label="Nachname"
              onBlur={handleBlur}
              validators={
                formValue(values, "hasAlimonyClaims") === "yes" ? [...requiredFieldDefault, ...textFieldDefault] : []
              }
              registerValidators={registerValidators}
              error={!!errors["alimonyObligatedPerson.familyName"]}
              helperText={errors["alimonyObligatedPerson.familyName"]}
              autoComplete="family-name"
              margin="normal"
              value={formValue(values, "alimonyObligatedPerson.familyName")}
              onChange={handleChange}
              disabled={formValue(values, "hasAlimonyClaims") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Verhältnis zu dieser Person"}
              fullWidth
              value={formValue(values, "alimonyObligatedPersonRelation")}
              onChange={handleChange}
              onBlur={handleBlur}
              name={`alimonyObligatedPersonRelation`}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              disabled={formValue(values, "hasAlimonyClaims") !== "yes"}
              registerValidators={registerValidators}
              validators={formValue(values, "hasAlimonyClaims") === "yes" ? requiredFieldDefault : []}
              error={!!errors["alimonyObligatedPersonRelation"]}
              helperText={errors["alimonyObligatedPersonRelation"]}
            >
              <MenuItem value={"Kind"}>Sie ist mein Kind</MenuItem>
              <MenuItem value={"Ehepartner"}>Sie ist mein Ehepartner/Expartner</MenuItem>
              <MenuItem value={"Lebenspartner"}>Sie ist mein Lebenspartner/Expartner</MenuItem>
              <MenuItem value={"Elternteil"}>Sie ist mein Vater/meine Mutter</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default AlimonyClaims;
